import React from 'react'
import { IntlProvider } from 'react-intl'
import { IntlContextConsumer } from 'gatsby-plugin-intl'

// ---------------------------------------------------------------------------
// Dev-only translation HMR
// ---------------------------------------------------------------------------
// gatsby-plugin-intl bakes translations into static pageContext at build time
// via Node.js require(), which doesn't hot-reload during development.
//
// This module imports translation files through webpack (via require.context)
// so they become part of the module graph. In development, we wrap page
// content with an inner IntlProvider that overrides the plugin's stale
// messages with fresh webpack-imported ones. In production, this is a no-op.
//
// Adding a new language: just drop a new .json file in src/intl/ — it will
// be picked up automatically by require.context.
// ---------------------------------------------------------------------------

declare const __webpack_require__: any
declare const require: {
  context(
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp,
  ): {
    keys(): string[]
    (key: string): Record<string, string>
  }
} & NodeJS.Require

type Messages = Record<string, Record<string, string>>

const loadMessages = (): Messages => {
  const ctx = require.context('./', false, /\.json$/)
  const msgs: Messages = {}
  ctx.keys().forEach((key: string) => {
    // key is like './en.json' — extract 'en'
    const lang = key.replace('./', '').replace('.json', '')
    msgs[lang] = ctx(key)
  })
  return msgs
}

const messages = loadMessages()

type Props = {
  children: React.ReactNode
}

/**
 * In development: wraps children with an IntlProvider using webpack-imported
 * messages so translation changes trigger HMR.
 * In production: renders children as-is (pageContext messages are fine).
 */
export const IntlHMRProvider = ({ children }: Props): React.ReactNode => {
  if (process.env.NODE_ENV !== 'development') {
    return <>{children}</>
  }

  return (
    <IntlContextConsumer>
      {(intl) => (
        <IntlProvider
          locale={intl.language}
          defaultLocale={intl.language}
          messages={messages[intl.language] || messages.en}
        >
          {children}
        </IntlProvider>
      )}
    </IntlContextConsumer>
  )
}
